import React, { useMemo } from 'react';
import {
  Alert,
  AlertIcon,
  Badge,
  Button,
  Text,
  Tooltip,
  Box,
  HStack,
  VStack,
  useColorModeValue,
  Icon,
} from '@chakra-ui/react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from 'recharts';
import { FiTrendingUp, FiTrendingDown, FiMinus } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePriceData } from '@/hooks/usePriceData';

// 定义 props，支持自定义警告文案和跳转
interface PriceDisplayProps {
  context?: string; // 页面上下文，e.g., "recharge", "invite", "dashboard"
  showLink?: boolean; // 是否显示跳转按钮
  variant?: 'full' | 'compact' | 'minimal' | 'navbar' | 'trend'; // 显示变体
  showWarning?: boolean; // 是否显示警告信息
  showChart?: boolean; // 是否显示价格趋势图表
  showChange?: boolean; // 是否显示24小时变化
}

const PriceDisplay: React.FC<PriceDisplayProps> = ({ 
  context = "general", 
  showLink = true, 
  variant = 'full',
  showWarning = true,
  showChart = false,
  showChange = true
}) => {
  const { priceData, isLoading, error } = usePriceData();
  const { t } = useTranslation('common');
  const currentPrice = priceData?.currentPrice || 0.99;
  const priceChange = priceData?.priceChange24h || 0;

  // 生成24小时价格数据（模拟）
  const priceHistory = useMemo(() => {
    const now = new Date();
    const data = [];
    for (let i = 23; i >= 0; i--) {
      const time = new Date(now.getTime() - i * 60 * 60 * 1000);
      const basePrice = 0.99;
      const variation = (Math.random() - 0.5) * 0.02; // ±1% 变化
      const price = basePrice + variation;
      data.push({
        time: time.getHours() + ':00',
        price: parseFloat(price.toFixed(4)),
      });
    }
    return data;
  }, []);

  // 变化趋势图标和颜色
  const getChangeIcon = () => {
    if (priceChange > 0) return FiTrendingUp;
    if (priceChange < 0) return FiTrendingDown;
    return FiMinus;
  };

  const getChangeColor = () => {
    if (priceChange > 0) return 'green';
    if (priceChange < 0) return 'red';
    return 'gray';
  };

  // 主题颜色适配
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // 状态背景颜色
  const blueBg = useColorModeValue("blue.50", "blue.900");
  const redBg = useColorModeValue("red.50", "red.900");
  const greenBg = useColorModeValue("green.50", "green.900");
  const yellowBg = useColorModeValue("yellow.50", "yellow.900");

  // 确定价格状态和文案
  const priceStatus = useMemo(() => {
    if (isLoading) {
      return { 
        status: "info" as const, 
        label: t('price.loading'), 
        message: t('price.loadingMessage'), 
        color: "blue" as const,
        bgColor: blueBg
      };
    }
    
    if (error) {
      return { 
        status: "error" as const, 
        label: t('price.error'), 
        message: t('price.errorMessage'), 
        color: "red" as const,
        bgColor: redBg
      };
    }

    if (currentPrice >= 0.99 && currentPrice <= 1.01) {
      return { 
        status: "success" as const, 
        label: t('price.stable'), 
        message: t('price.stableMessage'), 
        color: "green" as const,
        bgColor: greenBg
      };
    }
    
    if (currentPrice >= 0.95 && currentPrice < 0.99) {
      return {
        status: "warning" as const,
        label: t('price.slightDeviation'),
        message: t('price.slightDeviationLow'),
        color: "yellow" as const,
        bgColor: yellowBg,
        link: "/invite",
        linkText: t('price.goInvite'),
      };
    }
    
    if (currentPrice < 0.95) {
      return {
        status: "error" as const,
        label: t('price.severeDeviation'),
        message: context === "recharge" 
          ? t('price.severeDeviationLowRecharge')
          : t('price.severeDeviationLow'),
        color: "red" as const,
        bgColor: redBg,
        link: context === "recharge" ? "/reserve" : "/reserve",
        linkText: t('price.viewReserve'),
      };
    }
    
    if (currentPrice > 1.01 && currentPrice <= 1.05) {
      return {
        status: "warning" as const,
        label: t('price.slightDeviation'),
        message: t('price.slightDeviationHigh'),
        color: "yellow" as const,
        bgColor: yellowBg,
        link: "/recharge",
        linkText: t('price.goRecharge'),
      };
    }
    
    return {
      status: "error" as const,
      label: t('price.severeDeviation'),
      message: t('price.severeDeviationHigh'),
      color: "red" as const,
      bgColor: redBg,
      link: "/recharge",
      linkText: t('price.goRecharge'),
    };
  }, [currentPrice, isLoading, error, context, blueBg, redBg, greenBg, yellowBg]);

  // 根据变体渲染不同样式
  if (variant === 'navbar') {
    return (
      <Tooltip
        label={
          <VStack spacing={1} align="start">
            <Text fontSize="sm" fontWeight="bold">
              {t('price.currentPrice')}: {currentPrice.toFixed(4)} USDT
            </Text>
            {showChange && (
              <Text fontSize="sm">
                {t('price.change24h')}: {priceChange > 0 ? '+' : ''}{priceChange.toFixed(2)}%
              </Text>
            )}
            <Text fontSize="sm">{priceStatus.label}</Text>
            <Text fontSize="sm">{t('price.anchorLine')}: 1.0000 USDT</Text>
          </VStack>
        }
        placement="bottom"
        hasArrow
      >
        <HStack 
          spacing={3} 
          p={2} 
          borderRadius="md" 
          h="40px"
          _hover={{ opacity: 0.8 }}
          transition="all 0.2s"
          cursor="pointer"
          justify="space-between"
          align="center"
          minW="350px"
          maxW="450px"
          overflow="hidden"
        >
          <HStack spacing={1} align="center" flexShrink={0}>
            <Text fontSize="sm" fontWeight="bold" color={`${priceStatus.color}.500`} whiteSpace="nowrap">
              {currentPrice.toFixed(4)}
            </Text>
            <Text fontSize="xs" color="gray.500">
              USDT
            </Text>
          </HStack>
          {showChange && (
            <HStack spacing={1} align="center" flexShrink={0}>
              <Box
                as={getChangeIcon()}
                color={`${getChangeColor()}.500`}
                boxSize={3}
              />
              <Text fontSize="sm" color={`${getChangeColor()}.500`} fontWeight="medium">
                {priceChange > 0 ? '+' : ''}{priceChange.toFixed(2)}%
              </Text>
            </HStack>
          )}
          <HStack spacing={1} align="center" flexShrink={0}>
            <Text fontSize="sm" color={`${priceStatus.color}.500`} fontWeight="medium">
              {priceStatus.label}
            </Text>
            <Text fontSize="xs" color="gray.500">
              • {t('price.anchorLine')}: 1.0000
            </Text>
          </HStack>
        </HStack>
      </Tooltip>
    );
  }

  if (variant === 'minimal') {
    return (
      <HStack spacing={2} p={2} borderRadius="md" bg={priceStatus.bgColor}>
        <Text fontSize="sm" fontWeight="bold">
          {t('currency.dor')}: {currentPrice.toFixed(2)} {t('currency.usdt')}
        </Text>
        <Badge colorScheme={priceStatus.color} size="sm">
          {priceStatus.label}
        </Badge>
      </HStack>
    );
  }

  if (variant === 'trend') {
    const chartBg = useColorModeValue('gray.50', 'gray.800');
    
    return (
      <Box
        bg={bgColor}
        border="1px"
        borderColor={borderColor}
        borderRadius="lg"
        p={3}
        minW="280px"
        maxW="320px"
      >
        <VStack spacing={3} align="stretch">
          {/* 价格信息行 */}
          <HStack justify="space-between" align="center">
            <VStack align="start" spacing={0}>
              <Text fontSize="xs" color="gray.500" fontWeight="medium">
                {t('price.dorPrice')}
              </Text>
              <HStack spacing={2} align="center">
                <Text fontSize="lg" fontWeight="bold" color={textColor}>
                  {currentPrice.toFixed(4)}
                </Text>
                <Text fontSize="sm" color="gray.500">
                  USDT
                </Text>
              </HStack>
            </VStack>

            <VStack align="end" spacing={1}>
              <Badge colorScheme={priceStatus.color} size="sm">
                {priceStatus.label}
              </Badge>
              {showChange && (
                <HStack spacing={1}>
                  <Box
                    as={getChangeIcon()}
                    color={`${getChangeColor()}.500`}
                    boxSize={3}
                  />
                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    color={`${getChangeColor()}.500`}
                  >
                    {priceChange > 0 ? '+' : ''}{priceChange.toFixed(2)}%
                  </Text>
                </HStack>
              )}
            </VStack>
          </HStack>

          {/* 价格趋势图表 */}
          {showChart && (
            <Box h="60px" bg={chartBg} borderRadius="md" p={2}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={priceHistory}>
                  <CartesianGrid strokeDasharray="3 3" stroke={borderColor} opacity={0.3} />
                  <XAxis
                    dataKey="time"
                    tick={{ fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    domain={['dataMin - 0.005', 'dataMax + 0.005']}
                    tick={{ fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => value.toFixed(3)}
                  />
                  <RechartsTooltip
                    contentStyle={{
                      backgroundColor: bgColor,
                      border: `1px solid ${borderColor}`,
                      borderRadius: '8px',
                      fontSize: '12px',
                    }}
                    formatter={(value: number) => [value.toFixed(4), 'USDT']}
                    labelFormatter={(label) => `${label}`}
                  />
                  <Line
                    type="monotone"
                    dataKey="price"
                    stroke={currentPrice >= 0.99 ? '#38A169' : '#E53E3E'}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 3, fill: currentPrice >= 0.99 ? '#38A169' : '#E53E3E' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          )}

          {/* 锚定线指示器 */}
          <HStack justify="space-between" align="center">
            <Text fontSize="xs" color="gray.500">
              {t('price.anchorLine')}: 1.0000 USDT
            </Text>
            <Box
              w="8px"
              h="8px"
              borderRadius="full"
              bg={currentPrice >= 0.99 ? 'green.400' : 'red.400'}
            />
          </HStack>
        </VStack>
      </Box>
    );
  }

  if (variant === 'compact') {
    return (
      <Alert
        status={priceStatus.status}
        variant="subtle"
        borderRadius="md"
        p={3}
        bg={priceStatus.bgColor}
      >
        <AlertIcon />
        <HStack flex="1" justify="space-between">
          <VStack align="start" spacing={1}>
            <Text fontSize="md" fontWeight="bold">
              {t('currentPrice', { price: currentPrice.toFixed(2) })}
            </Text>
            {showWarning && (
              <Text fontSize="sm" color={textColor}>{priceStatus.message}</Text>
            )}
          </VStack>
          <VStack align="end" spacing={1}>
            <Badge colorScheme={priceStatus.color} fontSize="sm">
              {priceStatus.label}
            </Badge>
            {showLink && priceStatus.link && (
              <Button
                as={Link}
                to={priceStatus.link}
                size="sm"
                colorScheme="blue"
                variant="outline"
              >
                {priceStatus.linkText}
              </Button>
            )}
          </VStack>
        </HStack>
      </Alert>
    );
  }

  // 完整版本 (variant === 'full')
  return (
    <Alert
      status={priceStatus.status}
      variant="subtle"
      borderRadius="md"
      p={4}
      bg={priceStatus.bgColor}
      aria-label={`DOR price status: ${priceStatus.label}`}
    >
      <AlertIcon />
      <Box flex="1">
        <VStack align="start" spacing={2}>
          <HStack justify="space-between" w="full">
            <Text fontSize={{ base: "md", md: "xl" }} fontWeight="bold">
              {t('currentPrice', { price: currentPrice.toFixed(2) })}
            </Text>
            <Badge colorScheme={priceStatus.color} fontSize="sm">
              {priceStatus.label}
            </Badge>
          </HStack>
          
          {showWarning && (
            <Text fontSize={{ base: "sm", md: "md" }} color={textColor}>
              {priceStatus.message}
            </Text>
          )}
          
          {showLink && priceStatus.link && (
            <Tooltip label={`点击${priceStatus.linkText}以采取行动`}>
              <Button
                as={Link}
                to={priceStatus.link}
                size="sm"
                colorScheme="blue"
                aria-label={priceStatus.linkText}
              >
                {priceStatus.linkText}
              </Button>
            </Tooltip>
          )}
        </VStack>
      </Box>
    </Alert>
  );
};

export default PriceDisplay; 